import { observable, computed, extendObservable, action } from 'mobx';

export default class SaleEditStore {
  @observable page = 1;
  @observable offerData = {};
  @observable docs = [];

  user = null;

  constructor(user) {
    this.user = user;
  }

  @computed get isPage1Valid() {
    const data = this.offerData;
    return !!data.object_type;
  }

  @computed get isPage2Valid() {
    return true;
  }

  @computed get isPage3Valid() {
    return true;
  }

  @computed get isCommercial() {
    return this.offerData.offer_variant === 'commercial';
  }

  @computed get isResidential() {
    return this.offerData.offer_variant === 'residential';
  }

  // VAT section is shown only if a seller has a VAT number. Sales that are
  // already VAT-liable keep it visible so the agent can still see/untick it.
  @computed get sellerHasVatNumber() {
    const data = this.offerData;
    if (data.seller_type === 'company') {
      return !!data.seller_company_vat_id;
    }
    return (
      !!data.seller_vat_number ||
      (data.additional_sellers || []).some((s) => !!s.seller_vat_number)
    );
  }

  @computed get showVatSection() {
    return this.sellerHasVatNumber || !!this.offerData.vat_mandate;
  }

  @computed get showGoingConcernSection() {
    return (
      this.isCommercial && this.showVatSection && !!this.offerData.vat_mandate
    );
  }

  @computed get titleType() {
    if (!!this.offerData.title_type) {
      return this.offerData.title_type;
    }

    // full vs. sectional
    switch (this.offerData.object_type) {
      case 'flat':
        return 'sectional';
      default:
        return 'full';
    }
  }

  @action initialize(offerData, docs) {
    this.offerData = Object.assign({}, offerData);
    this.docs = (docs || []).slice();
    extendObservable(this.offerData, {
      // Page1
      offer_variant: offerData.offer_variant || 'residential',
      title_type: offerData.title_type || '',
      sales_reference_no: offerData.sales_reference_no || '',
      listing_no: offerData.listing_no || '',
      conveyancer_name: offerData.conveyancer_name || '',
      conveyancer_address: offerData.conveyancer_address || '',
      conveyancer_account: offerData.conveyancer_account || '',
      conveyancer_bank: offerData.conveyancer_bank || '',
      conveyancer_branch: offerData.conveyancer_branch || '',
      bonded_to: offerData.bonded_to || '',
      bond_account_number: offerData.bond_account_number || '',
      object_type: offerData.object_type || '',
      address: offerData.address || '',
      title_deed_no: offerData.title_deed_no || '',
      erf_number: offerData.erf_number || '',
      township: offerData.township || '',
      area: offerData.area || '',
      section_number: offerData.section_number || '',
      door_number: offerData.door_number || '',
      scheme_name: offerData.scheme_name || '',
      sectional_plan_number: offerData.sectional_plan_number || '',
      sectional_plan_area: offerData.sectional_plan_area || '',
      parking_bay_number: offerData.parking_bay_number || '',
      garden_number: offerData.garden_number || '',
      yard_number: offerData.yard_number || '',
      balcony_number: offerData.balcony_number || '',
      utility_room_number: offerData.utility_room_number || '',
      reservation_of_rights_of_extension:
        offerData.reservation_of_rights_of_extension || '',
      fixtures: offerData.fixtures || '',
      occupation: offerData.occupation || '',
      existing_tenants: offerData.existing_tenants || '',
      lease_agreement: offerData.lease_agreement || '',
      certificates: offerData.certificates || [],

      // Page2
      existing_mandate: offerData.existing_mandate || false,
      vat_mandate: offerData.vat_mandate || false,
      vat_percentage: offerData.vat_percentage,
      surety_required: offerData.surety_required || false,
      seller_type: offerData.seller_type || 'private',
      seller_company_name: offerData.seller_company_name || '',
      seller_vat_id: offerData.seller_company_vat_id || '',
      seller_firstName: offerData.seller_firstName || '',
      seller_lastName: offerData.seller_lastName || '',
      seller_personal_id: offerData.seller_personal_id || '',
      seller_tax_number: offerData.seller_tax_number || '',
      seller_vat_number: offerData.seller_vat_number || '',
      seller_company_vat_id: offerData.seller_company_vat_id || '',
      seller_street: offerData.seller_street || '',
      seller_email: offerData.seller_email || '',
      seller_phone_number: offerData.seller_phone_number || '',
      seller_marital_status: offerData.seller_marital_status || '',
      seller_marital_status_law: offerData.seller_marital_status_law || '',
      seller_marital_status_other: offerData.seller_marital_status_other || '',
      seller_is_resident: offerData.seller_is_resident || false,
      seller_house_number: offerData.seller_house_number || '',
      seller_street_additional: offerData.seller_street_additional || '',
      seller_zip: offerData.seller_zip || '',
      seller_city: offerData.seller_city || '',
      seller_country: offerData.seller_country || 'South Africa',
      seller_country_code: offerData.seller_country_code || 'ZA',
      seller_going_concern: offerData.seller_going_concern || false,
      seller_going_concern_income_earning:
        offerData.seller_going_concern_income_earning || false,
      seller_going_concern_separate_operation:
        offerData.seller_going_concern_separate_operation || false,
      // pre-populate keys so MobX tracks them on existing additional sellers
      additional_sellers: (offerData.additional_sellers || []).map((seller) =>
        Object.assign(
          { seller_tax_number: '', seller_vat_number: '' },
          seller
        )
      ),
      penalty: offerData.penalty || '',
      handover: offerData.handover || '',
    });
  }
}

export { SaleEditStore };



// WEBPACK FOOTER //
// ./src/app/@propnow/containers/Sale/SaleEditSection/_za/stores/index.js